import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import URI from 'urijs';
import { useCtrLogger } from '@bingads-webui-campaign/reports-v4-telemetry';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { getRoutePathById, siteMapPropType, withSiteMap, processor } from '@bingads-webui-campaign-react/with-site-map';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { TooltipHost, TooltipDelay, DirectionalHint, Link as LinkButton } from '@fluentui/react';
import { formatCurrency, getMismatchedInfoString, parseDecimal } from '../utils';
import { getBillingDocumentCellStyles } from '../styles';

export function BillingDocumentCellBase({
  i18n,
  siteMap,
  billingDocumentNumbers,
  mismatchedAmount,
  mismatchedReason,
  currencyCode,
}) {
  const classes = useDefaultStyles(getBillingDocumentCellStyles);
  const { scope: { values: { cid, aid, uid } } } = useContext(ScopeContext);
  const ctrLogger = useCtrLogger('ReportsV4PreviewReport');

  const mismatchedAmountNumber = parseDecimal({ i18n, value: mismatchedAmount });
  const mismatchedAmountString = formatCurrency({ i18n, value: mismatchedAmountNumber, currencyCode });
  const mismatchedInfoString = getMismatchedInfoString({ i18n, mismatchedReason, mismatchedAmountString });
  const showMismatchIcon = useMemo(() => !_.isEmpty(billingDocumentNumbers) && !_.isEmpty(mismatchedInfoString), [billingDocumentNumbers, mismatchedInfoString]);

  const bilingDocumentLink = ({ bdNumber }) => {
    const pathname = getRoutePathById(siteMap, 'transactionBillingDocuments');
    const searchParams = {
      aid,
      cid,
      uid,
      prefilter: `[{"Field":"SAPDocumentNumber","Operator":2,"Values":"${bdNumber}"}]`,
    };

    const link = new URI(pathname).addSearch(searchParams).toString();
    return link;
  };

  const onClickLink = ({ bdNumber }) => {
    ctrLogger.click({ target: 'billingDocumentNumber', extraData: { reportType: 'InsertionOrderReport', documentNumber: bdNumber } });
    return true;
  };

  const onHoverMismatchIcon = () => {
    ctrLogger.click({ target: 'mismatchIcon', extraData: { reportType: 'InsertionOrderReport', mismatchedAmountNumber } });
  };

  const tooltipProps = useMemo(() => ({
    onRenderContent: () => (
      <ReactTemplate
        model={{
          bold: text => <b>{text}</b>,
        }}
        template={mismatchedInfoString}
      />
    ),
  }), [mismatchedInfoString]);

  return (
    <div className={classnames('billing-document-cell', classes.BillingDocumentCell)}>
      <div className={classnames('billing-document-number-group', classes.BillingDocumentNumberGroup)}>
        {billingDocumentNumbers &&
          _.map(billingDocumentNumbers, billingDocumentNumber => (
            <LinkButton
              key={billingDocumentNumber}
              target="_blank"
              href={bilingDocumentLink({ bdNumber: billingDocumentNumber })}
              onClick={() => onClickLink({ bdNumber: billingDocumentNumber })}
            >
              {billingDocumentNumber}
            </LinkButton>
          ))}
      </div>
      {showMismatchIcon &&
        <TooltipHost
          tooltipProps={tooltipProps}
          delay={TooltipDelay.zero}
          id={`mismatch-information-tooltip-${billingDocumentNumbers.join('-')}`}
          directionalHint={DirectionalHint.rightCenter}
        >
          <Glyphicon
            className={classnames('mismatch-information-glyphicon', classes.MismatchInformation)}
            glyph="ba-Info"
            onMouseEnter={onHoverMismatchIcon}
          />
        </TooltipHost>}
    </div>
  );
}

BillingDocumentCellBase.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
  billingDocumentNumbers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  mismatchedAmount: PropTypes.string,
  mismatchedReason: PropTypes.string,
  currencyCode: PropTypes.string,
};

BillingDocumentCellBase.defaultProps = {
  billingDocumentNumbers: [],
  mismatchedAmount: null,
  mismatchedReason: null,
  currencyCode: null,
};

export const BillingDocumentCell = withSiteMap(
  BillingDocumentCellBase,
  [processor.processPermissions, processor.processPath]
);
